import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import apiClient from '@/lib/api-client'
import type { PublicProfile } from '@/types'
import { Loader2 } from 'lucide-react'
import { TemplateProvider } from '@/contexts/TemplateContext'
import { LazyTemplate } from '@/components/LazyTemplate'

// Template map - all use 'public' namespace for translations
const TEMPLATES = {
  beauty: () => import('@/components/templates/BeautyTemplate'),
  creative: () => import('@/components/templates/CreativeTemplate'),
  trade: () => import('@/components/templates/TradeTemplate'),
  photographer: () => import('@/components/templates/PhotographerTemplate'),
  'tattoo-artist': () => import('@/components/templates/TattooArtistTemplate'),
  florist: () => import('@/components/templates/FloristTemplate'),
  'artist-painter': () => import('@/components/templates/ArtistPainterTemplate'),
  'makeup-artist': () => import('@/components/templates/MakeupArtistTemplate'),
  'nail-technician': () => import('@/components/templates/NailTechnicianTemplate'),
  'hair-stylist': () => import('@/components/templates/HairStylistTemplate'),
  esthetician: () => import('@/components/templates/EstheticianTemplate'),
  'event-planner': () => import('@/components/templates/EventPlannerTemplate'),
  'tutor-teacher': () => import('@/components/templates/TutorTeacherTemplate'),
  'personal-trainer': () => import('@/components/templates/PersonalTrainerTemplate'),
  electrician: () => import('@/components/templates/ElectricianTemplate'),
  plumber: () => import('@/components/templates/PlumberTemplate'),
  'hvac-technician': () => import('@/components/templates/HVACTechnicianTemplate'),
  carpenter: () => import('@/components/templates/CarpenterTemplate'),
} as const

// Helper to check if profile has bookable services and availability
function hasBookingEnabled(profile: PublicProfile): boolean {
    return (
        profile.services !== undefined &&
        profile.services.length > 0 &&
        profile.availability !== undefined &&
        profile.availability.some((a) => a.is_available)
    )
}

export default function ProfilePage() {
    const { t } = useTranslation('public')
    const { username } = useParams<{ username: string }>()
    const navigate = useNavigate()

    const { data: profile, isLoading, error } = useQuery({
        queryKey: ['profile', username],
        queryFn: async () => {
            const response = await apiClient.get<{ data: PublicProfile }>(`/profiles/${username}`)
            return response.data.data
        },
        enabled: !!username,
    })

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        )
    }

    if (error || !profile) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900">{t('profile.notFound')}</h1>
                    <p className="text-gray-500 mt-2">
                        {t('profile.notFoundDescription')}
                    </p>
                </div>
            </div>
        )
    }

    // Render the appropriate template based on slug
    const renderTemplate = () => {
        const templateSlug = profile?.template?.slug

        switch (templateSlug) {
            case 'beauty':
                return <LazyTemplate importFn={TEMPLATES.beauty} namespace="public" profile={profile} />
            case 'creative':
                return <LazyTemplate importFn={TEMPLATES.creative} namespace="public" profile={profile} />
            case 'trade':
                return <LazyTemplate importFn={TEMPLATES.trade} namespace="public" profile={profile} />
            case 'photographer':
                return <LazyTemplate importFn={TEMPLATES.photographer} namespace="public" profile={profile} />
            case 'tattoo-artist':
                return <LazyTemplate importFn={TEMPLATES['tattoo-artist']} namespace="public" profile={profile} />
            case 'florist':
                return <LazyTemplate importFn={TEMPLATES.florist} namespace="public" profile={profile} />
            case 'artist-painter':
                return <LazyTemplate importFn={TEMPLATES['artist-painter']} namespace="public" profile={profile} />
            case 'makeup-artist':
                return <LazyTemplate importFn={TEMPLATES['makeup-artist']} namespace="public" profile={profile} />
            case 'nail-technician':
                return <LazyTemplate importFn={TEMPLATES['nail-technician']} namespace="public" profile={profile} />
            case 'hair-stylist':
                return <LazyTemplate importFn={TEMPLATES['hair-stylist']} namespace="public" profile={profile} />
            case 'esthetician':
                return <LazyTemplate importFn={TEMPLATES.esthetician} namespace="public" profile={profile} />
            case 'event-planner':
                return <LazyTemplate importFn={TEMPLATES['event-planner']} namespace="public" profile={profile} />
            case 'tutor-teacher':
                return <LazyTemplate importFn={TEMPLATES['tutor-teacher']} namespace="public" profile={profile} />
            case 'personal-trainer':
                return <LazyTemplate importFn={TEMPLATES['personal-trainer']} namespace="public" profile={profile} />
            case 'electrician':
                return <LazyTemplate importFn={TEMPLATES.electrician} namespace="public" profile={profile} />
            case 'plumber':
                return <LazyTemplate importFn={TEMPLATES.plumber} namespace="public" profile={profile} />
            case 'hvac-technician':
                return <LazyTemplate importFn={TEMPLATES['hvac-technician']} namespace="public" profile={profile} />
            case 'carpenter':
                return <LazyTemplate importFn={TEMPLATES.carpenter} namespace="public" profile={profile} />
            default:
                return <LazyTemplate importFn={TEMPLATES.beauty} namespace="public" profile={profile} />
        }
    }

    // Check if booking is enabled for this profile
    const canBook = hasBookingEnabled(profile)

    return (
        <TemplateProvider
            colorScheme={profile.color_scheme || 'modern-minimal'}
            typography={profile.typography || 'playfair-dm-sans'}
        >
            {renderTemplate()}

            {/* Book Now Button - Floating at bottom right */}
            {canBook && (
                <button
                    onClick={() => navigate(`/book/${username}`)}
                    className="fixed bottom-6 right-6 px-6 py-3 bg-indigo-600 text-white rounded-full font-medium shadow-lg hover:bg-indigo-700 transition-colors z-50 flex items-center gap-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    {t('profile.bookNow')}
                </button>
            )}
        </TemplateProvider>
    )
}
